/**
 * Primitivas compartilhadas do card do Monitor Unificado de campanhas
 * (`CampaignItemCard` + `CampaignItemPromotions`).
 *
 * Guards de nulo, linha de métrica, pílulas e mapas de badge ficam AQUI para não
 * duplicar formatação inline nos dois componentes (CLAUDE.md §4). Toda formatação
 * numérica delega para `utils/format.js` — nada de `Intl.NumberFormat` local.
 *
 * REGRA DO MONITOR (contrato §Dependência de sync): campo ausente/`null` NUNCA
 * vira 0 nem número estimado — vira '—'. Só valor real do backend é exibido.
 */
import { FormatDecimal1, FormatInt, FormatMoney } from '../../utils/format';

export const DASH = '—';

/** Sinal de menos tipográfico (U+2212) — alinha melhor que o hífen em tabular-nums. */
const MINUS = '−';

/** true só quando `value` é número finito de verdade (null/''/undefined → false). */
export function IsNum(value) {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    return Number.isFinite(Number(value));
}

/** Inteiro pt-BR; ausente → '—' (0 é valor legítimo e aparece como "0"). */
export function FormatIntOrDash(value) {
    return IsNum(value) ? FormatInt(value) : DASH;
}

/** "12,5%" (1 decimal); ausente → '—'. */
export function FormatPercentOrDash(value) {
    return IsNum(value) ? `${FormatDecimal1(value)}%` : DASH;
}

/** "12,5" (1 decimal); ausente → '—'. */
export function FormatDecimalOrDash(value) {
    return IsNum(value) ? FormatDecimal1(value) : DASH;
}

/**
 * Coluna 6 do card — tarifas derivadas (contrato §Fórmulas):
 *   fee_ml_display = fee_amount − fee_fixed     (a linha "ML (x%)" do Legacy)
 *   fee_total      = fee_amount + shipping_cost
 *   fee_total_pct  = fee_total / price_consumer * 100
 *
 * Vive aqui porque o card E a exportação CSV precisam exatamente destes três
 * números: duplicar a conta é como nasceu o bug #5 do contrato (tarifa
 * inventada no frontend). `feesPending` = o worker ainda não sincronizou
 * NENHUM dos dois lados — a coluna fica vazia em vez de exibir 0.
 */
export function ComputeItemFees(item = {}) {
    const feeAmount = IsNum(item.fee_amount) ? Number(item.fee_amount) : null;
    const feeFixed = IsNum(item.fee_fixed) ? Number(item.fee_fixed) : null;
    const shippingCost = IsNum(item.shipping_cost) ? Number(item.shipping_cost) : null;
    const priceConsumer = IsNum(item.price_consumer) ? Number(item.price_consumer) : null;

    const feeTotal = feeAmount === null && shippingCost === null ? null : (feeAmount || 0) + (shippingCost || 0);

    return {
        feeAmount,
        feeFixed,
        shippingCost,
        feeMlDisplay: feeAmount === null ? null : feeAmount - (feeFixed === null ? 0 : feeFixed),
        feeTotal,
        feeTotalPct: feeTotal !== null && priceConsumer ? (feeTotal / priceConsumer) * 100 : null,
        feesPending: feeAmount === null && shippingCost === null,
    };
}

const PILL_BASE = 'inline-flex max-w-full items-center gap-1 rounded-full leading-tight';

const PILL_TONES = {
    success: 'bg-success-soft text-success-text dark:bg-success-900/30 dark:text-success-300',
    warning: 'bg-warning-soft text-warning-text dark:bg-warning-900/30 dark:text-warning-300',
    danger: 'bg-danger-soft text-danger-text dark:bg-danger-900/30 dark:text-danger-300',
    info: 'bg-info-soft text-info-text dark:bg-info-900/30 dark:text-info-300',
    mono: 'bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-300',
    // Neutro para casos NÃO resolvidos da coluna Catálogo/Sugestão: só
    // "Ganhando" e "Já está na melhor" (casos resolvidos) usam `success`.
    neutral: 'bg-mono-100 text-mono-700 dark:bg-mono-800 dark:text-mono-200',
};

// TODA pílula de status tem o mesmo tamanho: 10px. Antes os casos "resolvidos"
// (Ganhando, Já está na melhor) usavam 11px em negrito, e ficavam
// desproporcionais ao lado dos demais status da mesma coluna — o destaque deles
// já vem da COR (verde), não precisa também de corpo maior.
const PILL_SIZE = 'px-1.5 py-px text-[10px] font-semibold';

/**
 * Pílula compacta (status, catálogo, atenção, sugestão).
 * SEM ícone: a coluna Catálogo/Sugestão pedia texto puro (regra do dono).
 */
export const Pill = ({ tone = 'mono', children, title, className = '' }) => (
    <span
        className={`${PILL_BASE} ${PILL_SIZE} ${PILL_TONES[tone] || PILL_TONES.mono} ${className}`}
        title={title}
    >
        <span className="truncate">{children}</span>
    </span>
);

/** Evento de `campaign_item_events` → rótulo curto e tom da pílula. Fonte única
 *  para a aba de auditoria e para o histórico dentro do card. */
export const EVENT_LABEL = { ENTERED: 'entrou', CHANGED: 'mudou', LEFT: 'saiu' };
export const EVENT_TONE = { ENTERED: 'success', CHANGED: 'warning', LEFT: 'mono' };

/** Par antes→depois, só quando algum dos dois existe. Riscado = valor antigo. */
export const BeforeAfter = ({ label, before, after, format = (x) => x }) => {
    if ((before === null || before === undefined) && (after === null || after === undefined)) {
        return null;
    }
    const changed = String(before ?? '') !== String(after ?? '');
    return (
        <span className="mr-4 whitespace-nowrap text-xs">
            <span className="text-mono-500">{label}: </span>
            <span className={changed ? 'line-through opacity-60' : ''}>{format(before)}</span>
            {changed ? <span className="font-semibold"> → {format(after)}</span> : null}
        </span>
    );
};

/** ml_status do anúncio → rótulo + tom da pílula do header. */
export const ML_STATUS_BADGES = {
    active: { label: 'Ativo', tone: 'success' },
    paused: { label: 'Pausado', tone: 'warning' },
    closed: { label: 'Encerrado', tone: 'danger' },
    under_review: { label: 'Em revisão', tone: 'info' },
    inactive: { label: 'Inativo', tone: 'mono' },
};

/** buybox_status (catálogo) → rótulo + tom. */
export const BUYBOX_BADGES = {
    winning: { label: 'Ganhando', tone: 'success' },
    sharing_first_place: { label: 'Dividindo', tone: 'warning' },
    competing: { label: 'Perdendo', tone: 'danger' },
    not_listed: { label: 'Não listado', tone: 'mono' },
};

/** attention_status → texto legível (coluna separada no contrato). */
export const ATTENTION_LABELS = {
    preco_invalido: 'Preço inválido',
    data_expirada: 'Data expirada',
    item_pausado_com_campanha_ativa: 'Pausado c/ campanha',
};

/**
 * Célula do grid de 6 colunas do card. Bordas: divide as colunas na horizontal
 * (`border-r`, sem borda na última) e as linhas quando o grid quebra (`border-b`).
 */
export const CardColumn = ({ label, labelTitle, sub, children, className = '' }) => (
    <div
        className={`flex min-w-0 flex-col justify-start border-b border-r border-mono-100 px-2 py-1.5 last:border-r-0 dark:border-mono-900 lg:border-b-0 ${className}`}
    >
        {label ? (
            <p
                className="mb-0.5 truncate text-[9px] font-bold uppercase tracking-wide text-mono-600"
                title={labelTitle}
            >
                {label}
            </p>
        ) : null}
        {children}
        {sub ? <p className="mt-px truncate text-[9px] italic text-mono-400">{sub}</p> : null}
    </div>
);

/**
 * Linha "rótulo à esquerda / valor à direita" — a unidade de densidade do card.
 *
 * `valueClass` default = BRANCO. Regra visual do dono: nas 6 colunas o conteúdo é
 * cinza (rótulo) + branco (valor); COR só nos percentuais (verde/vermelho). Não
 * passe `valueClass` colorido aqui — use `prefix` pro percentual colorido.
 */
export const MetricRow = ({ label, value, prefix = null, valueClass = 'text-black dark:text-white', title }) => (
    <div className="flex items-baseline justify-between gap-1.5 leading-tight" title={title}>
        <span className="shrink-0 text-[9px] uppercase tracking-wide text-mono-400">{label}</span>
        <span className="flex min-w-0 items-baseline justify-end gap-1">
            {prefix}
            <span className={`min-w-0 truncate text-right text-[11px] font-semibold tabular-nums ${valueClass}`}>
                {value}
            </span>
        </span>
    </div>
);

/**
 * Percentual como PREFIXO do valor (regra do dono: percentual vem SEMPRE antes,
 * nunca como sufixo). Verde quando positivo, vermelho quando negativo — é o
 * único elemento colorido das colunas do card.
 *
 * `signed` mostra o sinal explícito (+/−) — usado em variação/desconto; sem ele
 * o número sai puro entre parênteses (rebate, tarifa).
 */
export const PercentPrefix = ({ value, signed = false, invertColor = false, title }) => {
    if (!IsNum(value)) {
        return null;
    }
    const number = Number(value);
    const isPositive = number > 0;
    const isNegative = number < 0;
    // `invertColor`: em desconto/tarifa, número MAIOR é pior pro vendedor.
    const good = invertColor ? isNegative : isPositive;
    const bad = invertColor ? isPositive : isNegative;
    const tone = good
        ? 'text-success-600 dark:text-success-400'
        : bad
          ? 'text-danger-600 dark:text-danger-400'
          : 'text-mono-400';

    return (
        <span className={`shrink-0 text-[9px] font-semibold tabular-nums ${tone}`} title={title}>
            ({signed && isPositive ? '+' : ''}
            {signed && isNegative ? MINUS : ''}
            {FormatDecimal1(signed ? Math.abs(number) : number)}%)
        </span>
    );
};

/**
 * Prefixo monetário NEUTRO (9px, cinza) — mesmo corpo dos percentuais-prefixo.
 *
 * Existe pela regra do dono: o valor auxiliar vem SEMPRE antes do principal e
 * num corpo menor. Usado no Desc% da lista de campanhas, onde o auxiliar é o
 * desconto em R$ e o principal é o percentual. Sem cor de propósito — quem
 * comunica direção ali é o percentual.
 *
 * `dashWhenAbsent=false` some em vez de mostrar '—', para não duplicar o
 * símbolo quando o valor principal ao lado JÁ é '—'.
 */
export const MoneyPrefix = ({ value, title, dashWhenAbsent = true }) => {
    if (!IsNum(value)) {
        return dashWhenAbsent ? <span className="shrink-0 text-[9px] text-mono-400">{DASH}</span> : null;
    }
    return (
        <span className="shrink-0 text-[9px] font-semibold tabular-nums text-mono-400" title={title}>
            {FormatMoney(value)}
        </span>
    );
};

/**
 * Delta de dinheiro com sinal EXPLÍCITO ("+R$ 6,56" / "−R$ 6,96").
 *
 * Usado pelos indicadores de economia da lista de campanhas
 * (`consumer_savings` / `seller_gain`, contrato §Adições da 2ª rodada). A COR
 * sempre indica se é bom ou ruim: verde para ganho, vermelho para perda.
 *
 * O '—' é EXCLUSIVO de dois casos: a linha da campanha ATUAL (`isCurrentRow`),
 * onde comparar a campanha com ela mesma não quer dizer nada, e o dado ausente.
 * Outra campanha que empata com a atual (delta 0) mostra "R$ 0,00" em cinza
 * neutro: "dá na mesma" é uma resposta, e é diferente de "não se aplica" — antes
 * as duas saíam como '—' e a tabela escondia que a alternativa era equivalente.
 *
 * `signReflectsPrice` desacopla o SINAL da cor, e existe por causa do consumer:
 * o campo é `consumer_savings` (economia), então economizar R$ 6,56 chega como
 * +6,56 — mas escrever "+R$ 6,56" ao lado de um preço sugere preço MAIOR, o
 * oposto do que acontece. Com a flag, o sinal passa a descrever o movimento do
 * PREÇO: economia vira "−R$ 6,56" (fica mais barato) em verde, e encarecimento
 * vira "+R$ 3,00" (fica mais caro) em vermelho.
 *
 * No líquido do vendedor não se usa: lá "+" já significa ganhar mais, que é o
 * que se espera ler.
 */
export const DeltaMoney = ({
    value,
    title,
    zeroTitle,
    dashWhenAbsent = true,
    signReflectsPrice = false,
    isCurrentRow = false,
}) => {
    const isNumber = IsNum(value);
    const amount = isNumber ? Number(value) : 0;

    if (isCurrentRow) {
        return (
            <span className="text-mono-400" title={zeroTitle || title}>
                {DASH}
            </span>
        );
    }
    if (!isNumber) {
        // Como prefixo de um valor que TAMBÉM é '—' (campanha sem preço), o dash
        // do delta só duplicaria o símbolo ("— —"): melhor não renderizar nada.
        return dashWhenAbsent ? <span className="text-mono-400">{DASH}</span> : null;
    }
    if (amount === 0) {
        // Empate real com a campanha atual: cinza neutro, sem sinal — não é
        // ganho nem perda, e escrever "+R$ 0,00" sugeriria movimento.
        return (
            <span className="font-semibold tabular-nums text-mono-400" title={zeroTitle || title}>
                {FormatMoney(0)}
            </span>
        );
    }

    const isGain = amount > 0;
    // Cor pela natureza (ganho/perda); sinal pela direção que se quer comunicar.
    const showPlus = signReflectsPrice ? !isGain : isGain;
    return (
        <span
            className={`font-semibold tabular-nums ${
                isGain ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
            }`}
            title={title}
        >
            {showPlus ? '+' : MINUS}
            {FormatMoney(Math.abs(amount))}
        </span>
    );
};

/**
 * Comparativo semanal: últimos 7 dias vs. 8-14 dias (`sales_trend_percent`).
 * `null` (semana anterior = 0) → '—'; nunca 0% falso nem ∞.
 */
export const TrendBadge = ({ percent }) => {
    if (!IsNum(percent)) {
        return (
            <span className="text-[9px] font-semibold text-mono-400" title="Sem base de comparação (8-14 dias = 0)">
                {DASH}
            </span>
        );
    }
    // Sem ícone (regra do dono): só o número com sinal e cor.
    return (
        <PercentPrefix value={percent} signed title="Últimos 7 dias vs. 7 dias anteriores (8-14)" />
    );
};
